import React, { useRef, useEffect } from "react";
import { Box, Checkbox, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ReadinessChip from "../../../components/ReadinessChip/ReadinessChip";
import { TaskStatus } from "../../../utils/enums";

const TaskCard = ({
  task,
  isSelected,
  onClick,
  selectable = false,
  isChecked = false,
  onToggleChecked,
}) => {
  const theme = useTheme();
  const cardRef = useRef(null);

  const getProgressData = (progressData) => {
    if (!progressData) return null;
    if (typeof progressData === "object") return progressData;
    try {
      return JSON.parse(progressData);
    } catch {
      return null;
    }
  };

  const progressData = getProgressData(task.progress_data);

  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [isSelected]);

  return (
    <Box
      ref={cardRef}
      onClick={onClick}
      sx={{
        backgroundColor: isSelected
          ? alpha(theme.palette.primary.main, 0.05)
          : "#FFFFFF",
        border: `1px solid ${
          isSelected
            ? theme.palette.primary.main
            : alpha(theme.customColors.border.main, 0.3)
        }`,
        borderRadius: "8px",
        p: 2.5,
        pb: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: isSelected ? "0 2px 8px rgba(0, 0, 0, 0.08)" : "none",
        "&:hover": {
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
          borderColor: isSelected
            ? theme.palette.primary.main
            : alpha(theme.customColors.border.main, 0.5),
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.08)
            : "#FFFFFF",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: selectable ? 1 : 0,
          pb: 2,
        }}
      >
        {selectable && (
          <Checkbox
            size="small"
            checked={isChecked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleChecked?.(task.id)}
            inputProps={{ "aria-label": `Select task ${task.title}` }}
            sx={{ p: 0, mt: "1px", flexShrink: 0 }}
          />
        )}
        <Typography
          variant="body2"
          fontWeight={600}
          color={theme.customColors.text.strong}
          sx={{ lineHeight: 1.4, flex: 1 }}
        >
          {task.title}
        </Typography>
      </Box>

      {(() => {
        let percentage = 0;
        let statusStyles = {
          barColor: "#ef4444",
          backgroundColor: "#fee2e2",
        };

        if (task.task_type_key === "resolve_check" && progressData) {
          const total =
            (progressData.passed || 0) +
            (progressData.failed || 0) +
            (progressData.pending || 0) +
            (progressData.disabled || 0);
          const passingAndDisabled =
            (progressData.passed || 0) + (progressData.disabled || 0);
          percentage =
            total > 0 ? Math.round((passingAndDisabled / total) * 100) : 0;
        } else {
          percentage = task.status_key === TaskStatus.RESOLVED ? 100 : 0;
        }

        const getStatusStyles = (percentage) => {
          if (percentage >= 85) {
            return {
              barColor: "#10b981",
              backgroundColor: "#d1fae5",
            };
          } else if (percentage >= 60) {
            return {
              barColor: "#3b82f6",
              backgroundColor: "#dbeafe",
            };
          } else if (percentage >= 30) {
            return {
              barColor: "#f59e0b",
              backgroundColor: "#fef3c7",
            };
          } else {
            return {
              barColor: "#ef4444",
              backgroundColor: "#fee2e2",
            };
          }
        };

        statusStyles = getStatusStyles(percentage);

        return (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: theme.customColors.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Progress
              </Typography>
              <ReadinessChip percentage={percentage} />
            </Box>

            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  height: 4,
                  borderRadius: "2px",
                  backgroundColor: alpha(theme.customColors.border.main, 0.2),
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${percentage}%`,
                    backgroundColor: statusStyles.barColor,
                    borderRadius: "2px",
                    transition: "width 0.4s ease",
                  }}
                />
              </Box>
            </Box>
          </Box>
        );
      })()}
    </Box>
  );
};

export default TaskCard;
